/**
 * Knead Cats — free bed-cover promotion.
 *
 * The cover catalogue is loaded from Shopify at runtime. Promo cover lines are
 * tagged with private line-item properties so paid covers are never managed by
 * this script.
 */
(function () {
  'use strict';

  var CFG = window.KQ_FREE_COVER;
  if (!CFG || !CFG.COVER_PRODUCT_HANDLE) return;

  var syncing = false;
  var syncTimer = null;
  var pendingSync = false;
  var coverProductPromise = null;
  var previousBeds = null;
  var reconciliationPromise = null;
  var lastSyncError = null;
  var lastValidAt = 0;
  var lastReconcileStartedAt = 0;
  var syncGeneration = 0;
  var SYNC_STATE_KEY = 'kq_free_cover_sync';
  var SYNC_LOCK_KEY = 'kq_free_cover_sync_lock';
  var SYNC_LOCK_TTL_MS = 45000;
  var SYNC_COOLDOWN_MS = 2000;
  var VERIFICATION_PAUSE_MS = 15000;
  var RETRY_DELAYS = [0, 1000, 3000];
  var TAB_ID = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);

  function RetryableCartError(message, status, verification) {
    this.name = 'RetryableCartError';
    this.message = message;
    this.status = status || 0;
    this.verification = verification === true;
  }
  RetryableCartError.prototype = Object.create(Error.prototype);
  RetryableCartError.prototype.constructor = RetryableCartError;

  function readSyncState() {
    try {
      return JSON.parse(localStorage.getItem(SYNC_STATE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function markSyncPending(reason, attempts, blockedUntil) {
    try {
      localStorage.setItem(SYNC_STATE_KEY, JSON.stringify({
        pending: true,
        reason: reason || 'cart-change',
        attempts: attempts || 0,
        updatedAt: Date.now(),
        blockedUntil: blockedUntil || 0
      }));
    } catch (error) {}
  }

  function clearSyncPending() {
    try { localStorage.removeItem(SYNC_STATE_KEY); } catch (error) {}
  }

  function acquireSyncLock() {
    try {
      var now = Date.now();
      var current = JSON.parse(localStorage.getItem(SYNC_LOCK_KEY) || 'null');
      if (current && current.owner !== TAB_ID && current.expiresAt > now) return false;
      localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify({
        owner: TAB_ID,
        expiresAt: now + SYNC_LOCK_TTL_MS
      }));
      var confirmed = JSON.parse(localStorage.getItem(SYNC_LOCK_KEY) || 'null');
      return Boolean(confirmed && confirmed.owner === TAB_ID);
    } catch (error) {
      // Storage can be unavailable in privacy modes; the in-tab promise still
      // prevents duplicate reconciliation in that case.
      return true;
    }
  }

  function refreshSyncLock() {
    try {
      var current = JSON.parse(localStorage.getItem(SYNC_LOCK_KEY) || 'null');
      if (current && current.owner === TAB_ID) {
        current.expiresAt = Date.now() + SYNC_LOCK_TTL_MS;
        localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify(current));
      }
    } catch (error) {}
  }

  function releaseSyncLock() {
    try {
      var current = JSON.parse(localStorage.getItem(SYNC_LOCK_KEY) || 'null');
      if (current && current.owner === TAB_ID) localStorage.removeItem(SYNC_LOCK_KEY);
    } catch (error) {}
  }

  function delay(milliseconds) {
    return new Promise(function (resolve) { setTimeout(resolve, milliseconds); });
  }

  // Anchor promo-window checks to the server's clock instead of the visitor's.
  // SERVER_RENDERED_AT_TS is a Liquid-rendered Unix timestamp captured at page
  // build time; the visitor's Date.now() at script init is captured in the
  // same tick and the delta is reused for every status check. A missing or
  // invalid timestamp falls back to the visitor's clock (offset = 0), which
  // preserves the prior behaviour.
  var serverRenderedTs = Number(CFG.SERVER_RENDERED_AT_TS);
  var serverTimeOffsetMs = serverRenderedTs > 0 ? (serverRenderedTs * 1000 - Date.now()) : 0;

  function serverNow() {
    return new Date(Date.now() + serverTimeOffsetMs);
  }

  // Parse a wall-clock string ("YYYY-MM-DDTHH:MM:SS", no UTC offset) as the
  // local time in the given timezone and return the corresponding UTC epoch
  // (ms). Accepts an IANA timezone name (e.g. "America/Los_Angeles", which
  // observes DST automatically) or a fixed UTC offset (e.g. "-07:00", for
  // legacy compatibility). Returns NaN on malformed input.
  //
  // For IANA zones the offset is resolved in two passes via Intl so the
  // result is correct even when the wall-clock sits on the far side of a
  // DST transition from the probe instant. The single-pass approach drifts
  // by one hour whenever the probe instant lands in the opposite DST
  // regime (e.g. configuring 2026-03-08 04:00 America/Los_Angeles: the
  // naive probe puts the moment in PST, so the result is off by an hour).
  function offsetMinutesFor(tz, date) {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null;
    try {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var parts = {};
      var pieces = fmt.formatToParts(date);
      for (var i = 0; i < pieces.length; i++) {
        if (pieces[i].type !== 'literal') parts[pieces[i].type] = pieces[i].value;
      }
      var asUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
      return Math.round((asUTC - date.getTime()) / 60000);
    } catch (e) {
      return null;
    }
  }

  function parseFixedOffsetMinutes(zone) {
    var fixedMatch = /^([+-])(\d{1,2}):?(\d{2})$/.exec(zone);
    if (!fixedMatch) return null;
    var sign = fixedMatch[1] === '-' ? -1 : 1;
    return sign * (Number(fixedMatch[2]) * 60 + Number(fixedMatch[3]));
  }

  function parseInTimezone(isoLocal, tz) {
    var match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(isoLocal || ''));
    if (!match) return NaN;
    var year = +match[1];
    var month = +match[2];
    var day = +match[3];
    var hour = +match[4];
    var minute = +match[5];
    var second = match[6] ? +match[6] : 0;

    var zone = (typeof tz === 'string' && tz) ? tz : 'America/Los_Angeles';
    var probeMs = Date.UTC(year, month - 1, day, hour, minute, second);
    var isFixedOffset = parseFixedOffsetMinutes(zone) !== null;
    var offsetMin = null;

    if (!isFixedOffset) {
      // IANA timezone path: probe, then refine with the first estimate so the
      // result lands on the correct side of any DST transition.
      offsetMin = offsetMinutesFor(zone, new Date(probeMs));
      if (offsetMin !== null && !Number.isNaN(offsetMin)) {
        var refinedProbe = probeMs - offsetMin * 60000;
        var refinedOffset = offsetMinutesFor(zone, new Date(refinedProbe));
        if (refinedOffset !== null && !Number.isNaN(refinedOffset)) {
          offsetMin = refinedOffset;
        }
      }
    }

    if (offsetMin === null || Number.isNaN(offsetMin)) {
      var fixed = parseFixedOffsetMinutes(zone);
      if (fixed !== null) {
        offsetMin = fixed;
      } else {
        // Final fallback: Pacific Daylight Time.
        offsetMin = -7 * 60;
      }
    }

    return probeMs - offsetMin * 60000;
  }

  function promotionStatus() {
    if (CFG.PROMOTION_ENABLED === false) return 'ended';
    var startValue = CFG.PROMOTION_START_AT;
    var endValue = CFG.PROMOTION_END_AT;
    var tz = CFG.PROMOTION_TZ || 'America/Los_Angeles';
    if (!startValue && !endValue) return 'active';
    var start = startValue ? parseInTimezone(startValue, tz) : -Infinity;
    var end = endValue ? parseInTimezone(endValue, tz) : Infinity;
    if (Number.isNaN(start) || Number.isNaN(end) || start > end) return 'invalid';
    var now = serverNow().getTime();
    if (now < start) return 'before';
    if (now > end) return 'ended';
    return 'active';
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeSize(value) {
    var normalized = normalize(value);
    // Exact match first to avoid "X-Small" → "small" substring false positives.
    if (normalized === 'small') return 'small';
    if (normalized === 'large') return 'large';
    if (normalized.indexOf('small') !== -1 && normalized.indexOf('x-small') === -1) return 'small';
    if (normalized.indexOf('large') !== -1 && normalized.indexOf('x-large') === -1) return 'large';
    return normalized.split('|')[0].trim();
  }

  // Bed sizes that do not qualify for the free cover (e.g. 'large').
  // Excluded sizes never count toward bed snapshots, so the reconciliation
  // loop's coverTotal > bedTotal branch automatically purges any covers
  // already in the cart for that size — the customer can never check out
  // with a free cover the merchant isn't paying for. Sourced from CFG so
  // the merchant can add more excluded sizes without touching this file.
  function isExcludedBedSize(size) {
    var excluded = (CFG && CFG.EXCLUDED_SIZES) || [];
    if (!excluded || !excluded.length) return false;
    var normalized = normalizeSize(size);
    for (var i = 0; i < excluded.length; i++) {
      if (normalizeSize(excluded[i]) === normalized) return true;
    }
    return false;
  }

  function optionMap(item) {
    var result = {};
    var options = item.options_with_values || [];
    for (var i = 0; i < options.length; i++) {
      result[normalize(options[i].name)] = options[i].value;
    }
    return result;
  }

  function getOption(item, names) {
    var options = optionMap(item);
    for (var i = 0; i < names.length; i++) {
      if (options[names[i]]) return options[names[i]];
    }
    return '';
  }

  function getSize(item) {
    return normalizeSize(getOption(item, ['size']));
  }

  function getColor(item) {
    return normalize(getOption(item, ['color', 'colour']));
  }

  function isPromoCover(item) {
    if (!item) return false;
    // ONLY match by line-item property, never by handle.
    // Paid covers must not be mistaken for promo covers.
    return Boolean(
      item.properties && String(item.properties[CFG.PROP_KEY_PROMO]) === 'true'
    );
  }

  function isBed(item) {
    if (!item || isPromoCover(item)) return false;
    var handle = normalize(item.handle || (item.product && item.product.handle));
    if (handle === normalize(CFG.COVER_PRODUCT_HANDLE) || handle.indexOf('cover') !== -1) return false;

    // Optional allow-list: when configured, only these handles count as beds.
    // Guards against other type='bed' products (bed frames, accessories…)
    // accidentally receiving a free cover.
    var bedHandles = CFG.BED_PRODUCT_HANDLES;
    if (bedHandles && bedHandles.length) {
      var allowed = false;
      for (var h = 0; h < bedHandles.length; h++) {
        if (normalize(bedHandles[h]) === handle) { allowed = true; break; }
      }
      if (!allowed) return false;
    }

    var productType = normalize(item.product_type);
    var title = normalize(item.product_title || item.title);
    return productType === 'bed' || /(^|\s|[-–—])bed($|\s|[-–—])/.test(title);
  }

  var FETCH_TIMEOUT_MS = 15000;

  async function fetchJSON(url, options) {
    options = Object.assign({
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }, options || {});

    // AbortController timeout: a hung fetch must not wedge syncing=true forever.
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    if (!options.signal) options.signal = controller.signal;

    var response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      clearTimeout(timeoutId);
      throw new RetryableCartError(error.message || 'Network request failed');
    }
    clearTimeout(timeoutId);
    var contentType = normalize(response.headers.get('content-type'));
    var isVerification = response.status === 403 || response.status === 429 ||
      contentType.indexOf('text/html') !== -1;
    if (isVerification || response.status >= 500) {
      throw new RetryableCartError('Temporary cart response: ' + response.status, response.status, isVerification);
    }
    var responseText;
    try {
      responseText = await response.text();
    } catch (error) {
      throw new RetryableCartError(error.message || 'Response could not be read', response.status);
    }
    var responseBody;
    try {
      responseBody = JSON.parse(responseText);
    } catch (error) {
      throw new RetryableCartError('Expected JSON but received a verification response', response.status, true);
    }
    if (!response.ok) {
      var requestError = new Error(responseBody.description || ('Request failed: ' + response.status));
      requestError.status = response.status;
      throw requestError;
    }
    return responseBody;
  }

  // Shared retry loop: re-runs fn() on RetryableCartError with growing delays.
  // fn receives the zero-based attempt index (0 = first try, no delay).
  async function withRetry(fn) {
    var lastError;
    for (var attempt = 0; attempt < RETRY_DELAYS.length; attempt++) {
      if (attempt > 0) {
        await delay(lastError && lastError.verification ? VERIFICATION_PAUSE_MS : RETRY_DELAYS[attempt]);
      }
      try {
        refreshSyncLock();
        return await fn(attempt);
      } catch (error) {
        lastError = error;
        if (!(error instanceof RetryableCartError)) throw error;
      }
    }
    throw lastError;
  }

  async function fetchJSONWithRetry(url, options) {
    return withRetry(function () {
      return fetchJSON(url, options);
    });
  }

  function loadCoverProduct() {
    if (!coverProductPromise) {
      coverProductPromise = fetchJSONWithRetry('/products/' + encodeURIComponent(CFG.COVER_PRODUCT_HANDLE) + '.js')
        .then(function (product) {
          product.promoVariants = (product.variants || []).map(function (variant) {
            var values = {};
            for (var i = 0; i < product.options.length; i++) {
              var option = product.options[i];
              values[normalize(option.name)] = variant['option' + option.position];
            }
            return {
              id: String(variant.id),
              available: variant.available !== false,
              size: normalizeSize(values.size),
              color: normalize(values.color || values.colour),
              colorLabel: values.color || values.colour || ''
            };
          });
          return product;
        })
        .catch(function (error) {
          coverProductPromise = null;
          throw error;
        });
    }
    return coverProductPromise;
  }

  function findCoverVariant(product, size, color) {
    size = normalizeSize(size);
    color = normalize(color);
    for (var i = 0; i < product.promoVariants.length; i++) {
      var variant = product.promoVariants[i];
      if (variant.available && variant.size === size && variant.color === color) return variant;
    }
    return null;
  }

  function buildBedSnapshot(items) {
    var snapshot = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isBed(item)) continue;
      var size = getSize(item);
      var color = getColor(item);
      if (!size || !color) continue;
      // Excluded sizes (e.g. 'large') never qualify for the free cover.
      // Skipping them here means the reconciliation loop's
      // coverTotal > bedTotal branch will purge any covers already in the
      // cart for the excluded size on the next sync.
      if (isExcludedBedSize(size)) continue;
      if (!snapshot[size]) snapshot[size] = { total: 0, colors: {} };
      snapshot[size].total += item.quantity;
      snapshot[size].colors[color] = (snapshot[size].colors[color] || 0) + item.quantity;
    }
    return snapshot;
  }

  function buildCoverSnapshot(items, product) {
    var byId = {};
    for (var p = 0; p < product.promoVariants.length; p++) {
      byId[product.promoVariants[p].id] = product.promoVariants[p];
    }

    var snapshot = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isPromoCover(item)) continue;
      var variant = byId[String(item.variant_id)];
      if (!variant) continue;
      if (!snapshot[variant.size]) snapshot[variant.size] = { total: 0, colors: {} };
      if (!snapshot[variant.size].colors[variant.color]) snapshot[variant.size].colors[variant.color] = [];
      snapshot[variant.size].total += item.quantity;
      snapshot[variant.size].colors[variant.color].push({
        key: item.key,
        quantity: item.quantity,
        variant: variant
      });
    }
    return snapshot;
  }

  function changedBedColors(previous, current, size, direction) {
    var result = [];
    var before = previous && previous[size] ? previous[size].colors : {};
    var after = current[size] ? current[size].colors : {};
    var colors = {};
    Object.keys(before).concat(Object.keys(after)).forEach(function (color) { colors[color] = true; });
    Object.keys(colors).forEach(function (color) {
      var delta = (after[color] || 0) - (before[color] || 0);
      if ((direction > 0 && delta > 0) || (direction < 0 && delta < 0)) {
        result.push({ color: color, quantity: Math.abs(delta) });
      }
    });
    return result;
  }

  function planAdditions(size, quantity, beds, covers, product) {
    var additions = [];
    var bedColors = beds[size] ? beds[size].colors : {};
    var coverColors = covers[size] ? covers[size].colors : {};

    // Build per-colour deficits: how many covers of each colour are missing.
    var deficits = [];
    Object.keys(bedColors).forEach(function (color) {
      var covered = (coverColors[color] || []).reduce(function (sum, line) { return sum + line.quantity; }, 0);
      var deficit = Math.max(0, bedColors[color] - covered);
      if (deficit > 0) deficits.push({ color: color, quantity: deficit });
    });

    // If we have a valid previous snapshot, sort so colours that *increased*
    // come first — the customer just added those beds.
    if (previousBeds) {
      var changed = changedBedColors(previousBeds, beds, size, 1);
      deficits.sort(function (a, b) {
        var aChanged = changed.some(function (e) { return e.color === a.color; });
        var bChanged = changed.some(function (e) { return e.color === b.color; });
        return (bChanged ? 1 : 0) - (aChanged ? 1 : 0);
      });
    }

    for (var i = 0; i < deficits.length && quantity > 0; i++) {
      var variant = findCoverVariant(product, size, deficits[i].color);
      if (!variant) continue;
      var addQuantity = Math.min(quantity, deficits[i].quantity);
      additions.push({ variant: variant, quantity: addQuantity });
      quantity -= addQuantity;
    }

    // Fallback: still have quantity to allocate but no matching variant found.
    if (quantity > 0) {
      var fallbackColor = Object.keys(bedColors)[0];
      var fallback = findCoverVariant(product, size, fallbackColor);
      if (fallback) additions.push({ variant: fallback, quantity: quantity });
    }
    return additions;
  }

  function planRemovals(size, quantity, beds, covers) {
    var removals = [];
    var changed = changedBedColors(previousBeds, beds, size, -1);
    var coverColors = covers[size] ? covers[size].colors : {};
    // remainingByKey[key] = the quantity this line should KEEP after removal
    // (changeLine sets an absolute quantity, so a "removal" is lowering the
    // target value; a kept quantity of 0 removes the line entirely).
    var remainingByKey = {};

    function consumeColor(color, limit) {
      var lines = coverColors[color] || [];
      var allowed = Math.min(quantity, limit);
      for (var j = lines.length - 1; j >= 0 && allowed > 0; j--) {
        var remaining = remainingByKey[lines[j].key];
        if (typeof remaining === 'undefined') remaining = lines[j].quantity;
        var removeQuantity = Math.min(allowed, remaining);
        if (!removeQuantity) continue;
        remainingByKey[lines[j].key] = remaining - removeQuantity;
        allowed -= removeQuantity;
        quantity -= removeQuantity;
      }
    }

    for (var i = 0; i < changed.length && quantity > 0; i++) {
      consumeColor(changed[i].color, changed[i].quantity);
    }
    Object.keys(coverColors).forEach(function (color) {
      if (quantity > 0) consumeColor(color, quantity);
    });
    Object.keys(remainingByKey).forEach(function (key) {
      removals.push({ key: key, quantity: remainingByKey[key] });
    });
    return removals;
  }

  function lineProperties(size, color) {
    var properties = {};
    properties[CFG.PROP_KEY_PROMO] = 'true';
    properties[CFG.PROP_KEY_SIZE] = size;
    properties[CFG.PROP_KEY_COLOR] = color;
    return properties;
  }

  async function updateLines(updates) {
    var payload = {};
    (updates || []).forEach(function (update) {
      payload[update.key] = update.quantity;
    });
    if (!Object.keys(payload).length) return null;
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-KQ-Free-Cover': '1'
      },
      body: JSON.stringify({ updates: payload })
    });
  }

  async function addLine(variant, quantity) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-KQ-Free-Cover': '1'
      },
      body: JSON.stringify({
        id: Number(variant.id),
        quantity: quantity,
        properties: lineProperties(variant.size, variant.color)
      })
    });
  }

  async function addLines(additions) {
    if (!additions || !additions.length) return null;
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-KQ-Free-Cover': '1'
      },
      body: JSON.stringify({
        items: additions.map(function (addition) {
          return {
            id: Number(addition.variant.id),
            quantity: addition.quantity,
            properties: lineProperties(addition.variant.size, addition.variant.color)
          };
        })
      })
    });
  }

  // Shared cleanup helper: remove any promo-cover lines from the supplied
  // cart snapshot. Returns the number of lines purged. Used both by
  // doSync() (on cart / product pages) and cleanupStaleCoversIfInactive()
  // (any page load, between promo periods).
  async function purgeStalePromoCovers(cart, options) {
    options = options || {};
    var stale = cart.items.filter(isPromoCover);
    if (!stale.length) return 0;
    await updateLines(stale.map(function (item) {
      return { key: item.key, quantity: 0 };
    }));
    if (!options.skipRefresh) await refreshCartUI();
    previousBeds = null;
    return stale.length;
  }

  // Lightweight, fire-and-forget cleanup that runs on every page load
  // (including blog / homepage / collection pages where the rest of the
  // sync flow is dormant). When the promotion window is closed
  // ('before' / 'ended'), any promo-cover lines left in the cart from a
  // previous period (or added by a misbehaving tab) are purged so the
  // customer can't check out with a cover the merchant isn't paying for.
  // Errors are swallowed — cleanup is best-effort and must never break
  // the rest of the page.
  function cleanupStaleCoversIfInactive() {
    var status = promotionStatus();
    if (status !== 'before' && status !== 'ended') return Promise.resolve(false);
    return fetchJSON('/cart.js').then(function (cart) {
      return purgeStalePromoCovers(cart, { skipRefresh: true }).then(function (removed) {
        if (removed > 0) {
          lastValidAt = Date.now();
          console.log('[KQ Free Cover] Purged ' + removed + ' stale promo cover(s) (' + status + ' state)');
        }
        return removed > 0;
      });
    }).catch(function () {
      return false;
    });
  }

  async function doSync(options) {
    options = options || {};
    if (syncing) {
      pendingSync = true;
      return false;
    }
    syncing = true;
    pendingSync = false;
    lastSyncError = null;

    try {
      var cart = await fetchJSON('/cart.js');
      var status = promotionStatus();
      if (status === 'invalid') {
        // Misconfiguration (start >= end, NaN bounds, etc.). Don't touch
        // cart lines — let the merchant see the stale covers and fix the
        // config rather than have the script silently purge them.
        lastValidAt = Date.now();
        clearSyncPending();
        return true;
      }
      if (status === 'before' || status === 'ended') {
        // Outside the active window: any promo-cover lines in the cart are
        // stale (left over from a previous promo period or added by a
        // misbehaving tab) and must be removed, otherwise the customer
        // checks out with a cover the promo is not paying for.
        await purgeStalePromoCovers(cart, options);
        lastValidAt = Date.now();
        clearSyncPending();
        return true;
      }

      var product;
      try {
        product = await loadCoverProduct();
      } catch (loadError) {
        // Only remove promo lines when Shopify definitively says the product
        // does not exist. Transient network, verification, rate-limit, and
        // server failures must leave the customer's selected gifts intact.
        if (loadError.status !== 404) throw loadError;

        await purgeStalePromoCovers(cart, options);
        lastValidAt = Date.now();
        clearSyncPending();
        console.warn('[KQ Free Cover] Cover product unavailable:', loadError.message);
        return true;
      }

      var beds = buildBedSnapshot(cart.items);
      var covers = buildCoverSnapshot(cart.items, product);
      var sizes = {};
      Object.keys(beds).concat(Object.keys(covers)).forEach(function (size) { sizes[size] = true; });
      var removals = [];
      var additions = [];

      // Reconcile totals by size. Existing cover colours are customer choices
      // and must not be forced back to the bed colour.
      Object.keys(sizes).forEach(function (size) {
        var bedTotal = beds[size] ? beds[size].total : 0;
        var coverTotal = covers[size] ? covers[size].total : 0;
        if (coverTotal > bedTotal) {
          removals = removals.concat(planRemovals(size, coverTotal - bedTotal, beds, covers));
        } else if (bedTotal > coverTotal) {
          additions = additions.concat(planAdditions(size, bedTotal - coverTotal, beds, covers, product));
        }
      });

      // Shopify cart mutations are serialized into at most one batch update
      // and one batch add, preventing concurrent writes from racing.
      await updateLines(removals);
      await addLines(additions);

      var verifiedCart = (removals.length || additions.length) ? await fetchJSON('/cart.js') : cart;
      if (!cartCoverTotalsAreValid(verifiedCart.items, product)) {
        throw new RetryableCartError('Free cover reconciliation is incomplete');
      }
      // Only commit previousBeds after verification passes so retries see the
      // original delta, not a poisoned zero-delta snapshot.
      previousBeds = beds;
      lastValidAt = Date.now();
      clearSyncPending();
      if (!options.skipRefresh && (removals.length || additions.length)) {
        await refreshCartUI();
      }
      populateColorSelects(product);
      return true;
    } catch (error) {
      lastSyncError = error;
      markSyncPending('sync-failed', 0,
        error instanceof RetryableCartError && error.verification ? Date.now() + VERIFICATION_PAUSE_MS : 0);
      console.error('[KQ Free Cover] Sync failed:', error);
      return false;
    } finally {
      syncing = false;
      if (pendingSync && !options.skipFollowup) sync();
    }
  }

  function sync(options) {
    options = options || {};
    if (options.passive && Date.now() - lastValidAt < SYNC_COOLDOWN_MS) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncGeneration += 1;
    markSyncPending('cart-change');
    var cooldownRemaining = Math.max(0, lastReconcileStartedAt + SYNC_COOLDOWN_MS - Date.now());
    var wait = Math.max(Number(CFG.DEBOUNCE_MS) || 300, cooldownRemaining);
    syncTimer = setTimeout(function () {
      syncTimer = null;
      reconcileWithRetry();
    }, wait);
  }

  function reconcileWithRetry(options) {
    options = options || {};
    if (reconciliationPromise) return reconciliationPromise;
    if (!acquireSyncLock()) return Promise.resolve(false);
    lastReconcileStartedAt = Date.now();
    reconciliationPromise = withRetry(function (attempt) {
      markSyncPending(options.reason || 'reconcile', attempt);
      return waitForSyncIdle().then(function () {
        pendingSync = false;
        return doSync({
          skipRefresh: options.skipRefresh === true,
          skipFollowup: true
        });
      }).then(function (ok) {
        if (ok) return true;
        // Preserve the original error classification so verification responses
        // use the long pause; non-retryable failures abort immediately.
        if (lastSyncError) throw lastSyncError;
        throw new RetryableCartError('Reconciliation incomplete');
      });
    }).catch(function () { return false; }).finally(function () {
      reconciliationPromise = null;
      releaseSyncLock();
      if (pendingSync) sync();
    });
    return reconciliationPromise;
  }

  function waitForSyncIdle() {
    return new Promise(function (resolve) {
      var startedAt = Date.now();
      var timer = setInterval(function () {
        if (!syncing || Date.now() - startedAt > 10000) {
          clearInterval(timer);
          resolve(!syncing);
        }
      }, 50);
    });
  }

  function cartCoverTotalsAreValid(items, product) {
    var beds = buildBedSnapshot(items);
    var covers = buildCoverSnapshot(items, product);
    var sizes = {};
    Object.keys(beds).concat(Object.keys(covers)).forEach(function (size) { sizes[size] = true; });
    return Object.keys(sizes).every(function (size) {
      return (beds[size] ? beds[size].total : 0) === (covers[size] ? covers[size].total : 0);
    });
  }

  async function validateBeforeCheckout() {
    if (syncTimer) clearTimeout(syncTimer);
    markSyncPending('checkout');
    return Promise.race([
      reconcileWithRetry({ skipRefresh: true, reason: 'checkout' }),
      delay(5000).then(function () { return false; })
    ]);
  }

  async function changeCoverColor(lineIndexOrKey, newColor) {
    if (promotionStatus() !== 'active') return false;
    if (syncing && !await waitForSyncIdle()) return false;
    syncing = true;
    try {
      var product = await loadCoverProduct();
      var cart = await fetchJSON('/cart.js');
      // Prefer key-based lookup (avoids TOCTOU when lines shift between render and click).
      var item = null;
      if (typeof lineIndexOrKey === 'number' && lineIndexOrKey > 0) {
        item = cart.items[lineIndexOrKey - 1];
        // Cross-check: if the index-based item doesn't match a promo cover, fall back to key search.
        if (item && !isPromoCover(item)) item = null;
      }
      if (!item) {
        var key = String(lineIndexOrKey);
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) { item = cart.items[i]; break; }
        }
      }
      if (!item || !isPromoCover(item)) return false;

      var size = getSize(item) || normalizeSize(item.properties[CFG.PROP_KEY_SIZE]);
      var target = findCoverVariant(product, size, newColor);
      if (!target) return false;

      var targetLines = cart.items.filter(function (cartItem) {
        return isPromoCover(cartItem) && String(cartItem.variant_id) === target.id;
      });
      if (String(item.variant_id) === target.id && targetLines.length < 2) return true;

      var linesToReplace = targetLines.slice();
      if (!targetLines.some(function (line) { return line.key === item.key; })) {
        linesToReplace.push(item);
      }
      var totalQuantity = linesToReplace.reduce(function (total, line) {
        return total + line.quantity;
      }, 0);
      await updateLines(linesToReplace.map(function (line) {
        return { key: line.key, quantity: 0 };
      }));
      await addLine(target, totalQuantity);
      await refreshCartUI();
      sync();
      return true;
    } catch (error) {
      console.error('[KQ Free Cover] Color change failed:', error);
      return false;
    } finally {
      syncing = false;
    }
  }

  function colorsForSize(product, size) {
    var colors = [];
    var seen = {};
    size = normalizeSize(size);
    product.promoVariants.forEach(function (variant) {
      if (variant.available && variant.size === size && !seen[variant.color]) {
        seen[variant.color] = true;
        colors.push({ value: variant.colorLabel, normalized: variant.color });
      }
    });
    return colors;
  }

  async function buildProductAddRequest(formData) {
    if (!formData || typeof formData.get !== 'function' || location.pathname.indexOf('/products/') !== 0) return null;
    if (promotionStatus() !== 'active') return null;
    var variantId = String(formData.get('id') || '');
    var quantity = Number(formData.get('quantity') || 1);
    if (!variantId || quantity < 1) return null;

    var handle = location.pathname.split('/products/')[1];
    if (!handle) return null;
    handle = handle.split('/')[0].split('?')[0];
    var bedProduct = await fetchJSONWithRetry('/products/' + encodeURIComponent(handle) + '.js');
    var bedVariant = (bedProduct.variants || []).find(function (variant) {
      return String(variant.id) === variantId;
    });
    if (!bedVariant || !isBed({
      handle: bedProduct.handle,
      product_type: bedProduct.type,
      product_title: bedProduct.title
    })) return null;

    var values = {};
    (bedProduct.options || []).forEach(function (option) {
      values[normalize(option.name)] = bedVariant['option' + option.position];
    });
    // Bed size is excluded from the promotion (e.g. 'large'). Submit the form
    // natively so the customer still gets the bed, just no bundled cover.
    if (isExcludedBedSize(values.size)) return null;
    var coverProduct = await loadCoverProduct();
    var coverVariant = findCoverVariant(coverProduct, values.size, values.color || values.colour);
    if (!coverVariant) return null;

    var bedItem = { id: Number(variantId), quantity: quantity };
    var properties = {};
    formData.forEach(function (value, key) {
      var propertyMatch = key.match(/^properties\[(.+)\]$/);
      if (propertyMatch) properties[propertyMatch[1]] = value;
    });
    if (Object.keys(properties).length) bedItem.properties = properties;
    var sellingPlan = formData.get('selling_plan');
    if (sellingPlan) bedItem.selling_plan = Number(sellingPlan);

    return {
      items: [bedItem, {
        id: Number(coverVariant.id),
        quantity: quantity,
        properties: lineProperties(coverVariant.size, coverVariant.color)
      }],
      sections: formData.get('sections') || undefined,
      sections_url: formData.get('sections_url') || location.pathname
    };
  }

  function populateColorSelects(product) {
    var selects = document.querySelectorAll('.promo-cover-color-select');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      // Skip if already populated (prevents race on stale DOM)
      if (select.dataset.populated === 'true' && select.options.length > 1) continue;

      var row = select.closest('li, tr');
      if (!row) continue;
      var sizeText = '';
      var optionRows = row.querySelectorAll('.product-option:not(.promo-cover-color-option)');
      for (var o = 0; o < optionRows.length; o++) {
        var label = optionRows[o].querySelector('dt');
        if (label && normalize(label.textContent).indexOf('size') === 0) {
          var value = optionRows[o].querySelector('dd');
          sizeText = value ? value.textContent : '';
          break;
        }
      }
      var colors = colorsForSize(product, sizeText);
      if (!colors.length) continue;
      var current = normalize(select.dataset.currentColor);
      select.innerHTML = '';
      colors.forEach(function (color) {
        var option = document.createElement('option');
        option.value = color.value;
        option.textContent = color.value;
        option.selected = color.normalized === current;
        select.appendChild(option);
      });
      select.dataset.populated = 'true';
    }
  }

  async function refreshCartUI() {
    var mainCart = document.getElementById('main-cart-items');
    if (mainCart) {
      try {
        var response = await fetch('/cart?_kq=' + Date.now(), {
          headers: { Accept: 'text/html', 'X-KQ-Free-Cover': '1' },
          cache: 'no-store'
        });
        if (!response.ok) throw new Error('Cart HTML request failed: ' + response.status);
        var cartHtml = await response.text();
        if (normalize(response.headers.get('content-type')).indexOf('text/html') === -1 ||
            cartHtml.indexOf('main-cart-items') === -1) {
          throw new RetryableCartError('Cart HTML is temporarily unavailable', response.status);
        }
        var nextDocument = new DOMParser().parseFromString(cartHtml, 'text/html');
        [
          { id: 'main-cart-items', selector: '.js-contents' },
          { id: 'main-cart-footer', selector: '.js-contents' },
          { id: 'cart-icon-bubble', selector: null },
          { id: 'mobile-cart-icon-bubble', selector: null },
          { id: 'cart-live-region-text', selector: null }
        ].forEach(function (entry) {
          var currentContainer = document.getElementById(entry.id);
          var nextContainer = nextDocument.getElementById(entry.id);
          if (!currentContainer || !nextContainer) return;
          var currentTarget = entry.selector ? currentContainer.querySelector(entry.selector) : currentContainer;
          var nextTarget = entry.selector ? nextContainer.querySelector(entry.selector) : nextContainer;
          if (currentTarget && nextTarget) currentTarget.innerHTML = nextTarget.innerHTML;
        });
      } catch (error) {
        console.error('[KQ Free Cover] Main cart refresh failed:', error);
        if (error instanceof RetryableCartError) sync();
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { open: false, source: 'kq-free-cover-sync' }
    }));
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kq-free-cover-sync' });
    }
    // Re-populate colour selects after the section HTML has been refreshed.
    // The timeout accounts for the cart.js section re-render (network fetch + DOM swap).
    loadCoverProduct().then(function (product) {
      setTimeout(function () { populateColorSelects(product); }, 800);
      // Double-tap: catch late-arriving section HTML from slow networks.
      setTimeout(function () { populateColorSelects(product); }, 1800);
    });
  }

  function init() {
    // Always purge stale promo covers, even on pages that don't otherwise
    // need the sync machinery (homepage, blog, collection pages, etc.). A
    // returning visitor between promo periods would otherwise carry
    // leftover covers into the cart on the next promo start.
    cleanupStaleCoversIfInactive();

    // Only activate the full sync flow on pages that actually have cart
    // interaction elements. This prevents unnecessary API calls and event
    // listener registrations on pages like the blog, homepage, etc.
    var onCartPage = document.querySelector('cart-items') || document.querySelector('mini-cart');
    var onProductPage = document.querySelector('[data-type="add-to-cart-form"]');
    if (!onCartPage && !onProductPage) return;

    var checkoutInProgress = false;
    var productAddInProgress = false;
    // One-shot guard: requestSubmit() re-dispatches a submit event, which would
    // otherwise re-enter handleProductAdd and loop forever (each round firing
    // extra /products/*.js requests). The flag is consumed by the submit
    // listener below; the setTimeout backstop clears it in case HTML form
    // validation prevents the submit event from ever firing.
    var skipNextNativeSubmit = false;
    async function continueToCheckout(continuation, button) {
      if (checkoutInProgress) return;
      checkoutInProgress = true;
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      try {
        // Run one best-effort sync before checkout.  DO NOT block checkout
        // on sync success — OOS cover variants, network issues, or API
        // errors must never prevent a customer from paying.
        await validateBeforeCheckout().catch(function () { /* best-effort */ });
        continuation();
      } finally {
        checkoutInProgress = false;
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    loadCoverProduct().then(populateColorSelects).catch(function (error) {
      console.error('[KQ Free Cover] Cover product could not be loaded:', error);
    });

    function handleProductAdd(event, productForm, submitButton) {
      if (productAddInProgress || location.pathname.indexOf('/products/') !== 0) return false;
      productAddInProgress = true;
      markSyncPending('product-add');
      event.preventDefault();
      event.stopPropagation();
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('loading');
      }
      var formData = new FormData(productForm);
      buildProductAddRequest(formData).then(function (combinedRequest) {
        if (!combinedRequest) {
          // Not a bed product — submit the form natively so the existing
          // AJAX add-to-cart + drawer flow works unchanged.
          productAddInProgress = false;
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('loading');
          }
          skipNextNativeSubmit = true;
          productForm.requestSubmit(submitButton);
          // requestSubmit() dispatches the submit event synchronously, so the
          // listener above consumes the flag within this call stack. If HTML
          // validation blocked the event, clear the guard synchronously —
          // no timer timing guarantees involved.
          if (skipNextNativeSubmit) skipNextNativeSubmit = false;
          return;
        }
        return fetchJSON('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-KQ-Free-Cover': '1'
          },
          body: JSON.stringify(combinedRequest)
        }).then(function () {
          // Verify the server-side cart before navigating. This prevents a
          // stale cart-page snapshot from showing the bed before its cover.
          // Do not trap the customer on the PDP if Cloudflare remains active;
          // the durable pending marker resumes reconciliation on the cart.
          return Promise.race([
            reconcileWithRetry({ skipRefresh: true, reason: 'product-add-verify' }),
            delay(5000).then(function () { return false; })
          ]);
        }).then(function () {
          window.location.assign(theme.routes.cart_url || '/cart');
        });
      }).catch(function (error) {
        console.error('[KQ Free Cover] Combined product add failed:', error);
        productAddInProgress = false;
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('loading');
        }
        window.alert('The bed could not be added. Please try again.');
      });
      return true;
    }

    function wrapCartItems() {
      var CartItemsElement = window.customElements && window.customElements.get('cart-items');
      if (!CartItemsElement || CartItemsElement.prototype.kqFreeCoverWrapped) return;
      var originalUpdateQuantity = CartItemsElement.prototype.updateQuantity;
      CartItemsElement.prototype.updateQuantity = function () {
        markSyncPending('cart-quantity');
        var generationBeforeUpdate = syncGeneration;
        var result = originalUpdateQuantity.apply(this, arguments);
        // Some theme quantity paths use section rendering without emitting the
        // public cart event. A short debounced backstop keeps cover totals in
        // sync; concurrent calls collapse into the shared reconciliation.
        setTimeout(function () {
          if (syncGeneration === generationBeforeUpdate && !reconciliationPromise) sync();
        }, 700);
        return result;
      };
      CartItemsElement.prototype.kqFreeCoverWrapped = true;
    }
    wrapCartItems();
    if (window.customElements && !window.customElements.get('cart-items')) {
      window.customElements.whenDefined('cart-items').then(wrapCartItems);
    }

    if (!window.fetch.kqFreeCoverWrapped) {
      var originalFetch = window.fetch;
      var wrappedFetch = function (input, options) {
        var requestUrl = typeof input === 'string' ? input : (input && input.url) || '';
        var headers = options && options.headers;
        var internalRequest = headers && (
          headers['X-KQ-Free-Cover'] === '1' ||
          (typeof headers.get === 'function' && headers.get('X-KQ-Free-Cover') === '1')
        );
        if (!internalRequest && /\/cart\/add(\.js)?(?:\?|$)/.test(requestUrl) &&
            options && typeof FormData !== 'undefined' && options.body instanceof FormData) {
          markSyncPending('external-cart-add');
          return buildProductAddRequest(options.body).then(function (combinedRequest) {
            if (!combinedRequest) return originalFetch.call(window, input, options);
            var combinedOptions = Object.assign({}, options, {
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/javascript',
                'X-KQ-Free-Cover': '1'
              },
              body: JSON.stringify(combinedRequest)
            });
            return originalFetch.call(window, input, combinedOptions);
          });
        }
        var result = originalFetch.apply(this, arguments);
        if (!internalRequest && /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(requestUrl)) {
          markSyncPending('external-cart-change');
          var generationBeforeRequest = syncGeneration;
          result.then(function (response) {
            if (response.ok) setTimeout(function () {
              if (syncGeneration === generationBeforeRequest && !reconciliationPromise) sync();
            }, 700);
          }).catch(function () {});
        }
        return result;
      };
      wrappedFetch.kqFreeCoverWrapped = true;
      window.fetch = wrappedFetch;
    }

    document.addEventListener('ajaxProduct:added', sync);
    document.addEventListener('cartdrawer:opened', function () { sync({ passive: true }); });
    function resumePendingSync() {
      var state = readSyncState();
      if (state && state.pending && (!state.blockedUntil || state.blockedUntil <= Date.now())) {
        reconcileWithRetry({ reason: 'resume' });
      }
    }
    window.addEventListener('pageshow', resumePendingSync);
    window.addEventListener('online', resumePendingSync);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') resumePendingSync();
    });
    // Only wake another tab after the active owner releases its lock. Pending
    // marker writes themselves must not cause every open tab to sync.
    window.addEventListener('storage', function (event) {
      if (event.key === SYNC_LOCK_KEY && !event.newValue) resumePendingSync();
    });
    document.addEventListener('click', function (event) {
      var addButton = event.target.closest && event.target.closest(
        'form[data-type="add-to-cart-form"] button[name="add"]'
      );
      if (!addButton) return;
      handleProductAdd(event, addButton.form, addButton);
    }, true);
    document.addEventListener('submit', function (event) {
      if (skipNextNativeSubmit) {
        skipNextNativeSubmit = false;
        return;
      }
      var productForm = event.target;
      if (!productForm.matches || !productForm.matches('[data-type="add-to-cart-form"]') ||
          location.pathname.indexOf('/products/') !== 0) return;
      var submitButton = event.submitter || productForm.querySelector('[type="submit"]');
      handleProductAdd(event, productForm, submitButton);
    }, true);
    document.addEventListener('click', function (event) {
      var checkoutLink = event.target.closest && event.target.closest('a[href*="/checkout"]');
      if (!checkoutLink || checkoutInProgress) return;
      // Only intercept checkout links inside cart surfaces — a bare
      // a[href*="/checkout"] could match unrelated site links (help pages…).
      if (!checkoutLink.closest('#main-cart-footer, #mini-cart, cart-items, .cart__footer')) return;
      event.preventDefault();
      event.stopPropagation();
      continueToCheckout(function () {
        window.location.assign(checkoutLink.href);
      }, checkoutLink);
    }, true);
    document.addEventListener('submit', function (event) {
      var submitter = event.submitter || document.activeElement;
      if (!submitter || submitter.name !== 'checkout' || checkoutInProgress) return;
      event.preventDefault();
      event.stopPropagation();
      var form = event.target;
      continueToCheckout(function () {
        // Preserve the submitter name/value: Shopify's cart controller
        // requires `checkout` in the POST body to redirect to /checkout.
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = submitter.name;
        input.value = submitter.value || 'Checkout';
        form.appendChild(input);
        form.submit();
        form.removeChild(input);
      }, submitter);
    }, true);
    document.addEventListener('change', function (event) {
      var select = event.target.closest && event.target.closest('.promo-cover-color-select');
      if (!select) return;
      event.stopPropagation(); // prevent cart-items from treating this as a quantity change
      event.preventDefault();

      var lineIndex = Number(select.dataset.lineIndex);
      var newColor = select.value;
      if (!lineIndex || !newColor) return;

      // Use line key as primary identifier (avoids TOCTOU with index-based lookup).
      var lineKey = select.dataset.lineKey;
      var identifier = lineKey || lineIndex;
      changeCoverColor(identifier, newColor).then(function (success) {
        if (!success) {
          // Only roll back if the previous colour still exists among the
          // current options — populateColorSelects may have rebuilt the list.
          var current = select.dataset.currentColor;
          var hasCurrent = Array.prototype.some.call(select.options, function (o) {
            return o.value === current;
          });
          if (hasCurrent) select.value = current;
        }
      });
    }, true);
    document.addEventListener('cart:refresh', function (event) {
      if (!event.detail || event.detail.source !== 'kq-free-cover-sync') sync();
    });
    sync();
  }

  window.KQFreeCoverSync = {
    sync: sync,
    validateBeforeCheckout: validateBeforeCheckout,
    changeCoverColor: changeCoverColor,
    isPromoCover: isPromoCover
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
